import React, { useMemo, useState } from 'react';
import { FileUp, Percent, Plus } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ErrorState, PageHeader, SegmentedControl } from '../../components/app';
import { periodToParams, useCashflow } from './hooks/useCashflow';
import {
  AccrualDialog,
  AccrualsTable,
  CashflowCards,
  CashflowCharts,
  CommissionsDialog,
  ProvidersTable,
  SettlementDialog,
  SettlementsTable,
  StatementImportDialog,
} from './components';
import { CashflowTab, PeriodPreset } from './types';
import { formatMoney } from './format';

const PERIODS: Array<{ value: PeriodPreset; label: string }> = [
  { value: 'all', label: 'Весь период' },
  { value: '7d', label: '7 дней' },
  { value: '30d', label: '30 дней' },
  { value: 'month', label: 'Этот месяц' },
  { value: 'prevMonth', label: 'Прошлый месяц' },
];

/** Вкладки нижнего блока: деньги на счету, продажи, из которых они складываются, и графики. */
const TABS: Array<{ key: CashflowTab; title: string; hint: string }> = [
  {
    key: 'settlements',
    title: 'Поступления',
    hint: 'Зачисления на счёт — из выписки банка (метка «банк») или вручную по отчёту провайдера',
  },
  {
    key: 'accruals',
    title: 'Начисления',
    hint: 'Продажи у провайдеров без платежей в базе (Apple) — по одной записи на покупку',
  },
  {
    key: 'charts',
    title: 'Графики',
    hint: 'Сколько собрано по провайдерам и как это распределилось по дням, в сумах',
  },
];

const MONTH_GENITIVE = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
const MONTH_NOMINATIVE = new Intl.DateTimeFormat('ru-RU', { month: 'long' });

const parseDay = (iso: string) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
};

/** «1–31 октября 2026», «25 сентября 2026 – 31 октября 2026», «за всё время». */
function rangeCaption(preset: PeriodPreset): string {
  const { from, to } = periodToParams(preset);
  if (!from || !to) return 'За всё время';
  const a = parseDay(from);
  const b = parseDay(to);
  if (a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth()) {
    return `${a.getDate()}–${MONTH_GENITIVE.format(b)}`.replace(/\s?г\.$/, '');
  }
  return `${MONTH_GENITIVE.format(a)} – ${MONTH_GENITIVE.format(b)}`.replace(/\s?г\./g, '');
}

/** Хвост подписи в карточках цепочки: «за октябрь», «за 7 дней». */
function periodHint(preset: PeriodPreset): string {
  const { from } = periodToParams(preset);
  switch (preset) {
    case '7d':
      return 'за 7 дней';
    case '30d':
      return 'за 30 дней';
    case 'month':
    case 'prevMonth':
      return from ? `за ${MONTH_NOMINATIVE.format(parseDay(from))}` : 'за месяц';
    default:
      return 'за всё время';
  }
}

export default function CashflowPage() {
  const {
    overview,
    providers,
    isLoading,
    loadError,
    period,
    setPeriod,
    isSettlementDialogOpen,
    setIsSettlementDialogOpen,
    isCommissionsDialogOpen,
    setIsCommissionsDialogOpen,
    editingSettlement,
    formData,
    setFormData,
    isSubmitting,
    openCreate,
    openEdit,
    handleSubmit,
    handleDelete,
    tab,
    setTab,
    isAccrualDialogOpen,
    setIsAccrualDialogOpen,
    editingAccrual,
    accrualForm,
    setAccrualForm,
    openAccrualCreate,
    openAccrualEdit,
    handleAccrualSubmit,
    handleAccrualDelete,
    handleUpdateProvider,
    refresh,
  } = useCashflow();
  const [isImportOpen, setIsImportOpen] = useState(false);

  /** Начисления заводятся только у ручных провайдеров (см. ADR 0021). */
  const manualProviders = providers.filter((p) => p.is_active && p.source === 'manual');

  const total = overview?.total || { gross: 0, net: 0, settled: 0, outstanding: 0 };
  const providerRows = overview?.providers || [];
  const hasForeign = providerRows.some((p) => p.currency !== 'UZS');

  // Последнее Поступление по провайдеру — из списка за выбранный период.
  const lastSettlement = useMemo(() => {
    const out: Record<string, string> = {};
    for (const s of overview?.settlements ?? []) {
      if (!out[s.provider_id] || s.date > out[s.provider_id]) out[s.provider_id] = s.date;
    }
    return out;
  }, [overview]);

  const activeTab = TABS.find((item) => item.key === tab);

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Сверка поступлений"
        subtitle="Сколько провайдеры собрали, удержали, перечислили на счёт и ещё должны"
        actions={
          <>
            <Button variant="secondary" onClick={() => setIsCommissionsDialogOpen(true)}>
              <Percent aria-hidden />
              Ставки комиссии
            </Button>
            <Button variant="secondary" onClick={openCreate}>
              <Plus aria-hidden />
              Поступление вручную
            </Button>
            <Button onClick={() => setIsImportOpen(true)}>
              <FileUp aria-hidden />
              Импорт выписки
            </Button>
          </>
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <SegmentedControl ariaLabel="Период" value={period} onChange={setPeriod} options={PERIODS} className="flex-wrap" />
        <span className="text-[13px] text-text-2">
          {rangeCaption(period)} · суммы в сумах{hasForeign && ', доллары Apple переведены по Курсу записи'}
        </span>
      </div>

      {loadError ? (
        <Card>
          <ErrorState title="Не удалось загрузить сверку" message={loadError} onRetry={refresh} />
        </Card>
      ) : (
        <>
          <CashflowCards total={total} hasForeign={hasForeign} periodLabel={periodHint(period)} isLoading={isLoading} />

          <ProvidersTable
            providers={providerRows}
            total={total}
            lastSettlement={lastSettlement}
            isLoading={isLoading}
            formatMoney={formatMoney}
          />

          <Card className="overflow-hidden">
            <Tabs value={tab} onValueChange={(value) => setTab(value as CashflowTab)}>
              <div className="flex flex-wrap items-end justify-between gap-3 px-[18px] pt-3">
                <TabsList variant="underline" className="border-b-0">
                  {TABS.map((item) => (
                    <TabsTrigger key={item.key} value={item.key}>
                      {item.title}
                      {item.key === 'settlements' && overview && (
                        <span className="tabular-nums text-text-2">{overview.settlements.length}</span>
                      )}
                      {item.key === 'accruals' && overview && (
                        <span className="tabular-nums text-text-2">{overview.accruals.length}</span>
                      )}
                    </TabsTrigger>
                  ))}
                </TabsList>
                {tab === 'accruals' && (
                  <Button variant="secondary" size="sm" className="mb-2" onClick={openAccrualCreate}>
                    <Plus aria-hidden />
                    Добавить начисление
                  </Button>
                )}
              </div>
              <p className="m-0 border-t border-border-soft px-[18px] py-3 text-[13px] text-text-2">{activeTab?.hint}</p>

              <TabsContent value="settlements" className="mt-0">
                <SettlementsTable
                  settlements={overview?.settlements || []}
                  isLoading={isLoading}
                  onEdit={openEdit}
                  onDelete={handleDelete}
                  formatMoney={formatMoney}
                />
              </TabsContent>
              <TabsContent value="accruals" className="mt-0">
                <AccrualsTable
                  accruals={overview?.accruals || []}
                  isLoading={isLoading}
                  onEdit={openAccrualEdit}
                  onDelete={handleAccrualDelete}
                  formatMoney={formatMoney}
                />
              </TabsContent>
              <TabsContent value="charts" className="mt-0 px-[18px] pb-[18px]">
                <CashflowCharts providers={providerRows} daily={overview?.daily || []} formatMoney={formatMoney} />
              </TabsContent>
            </Tabs>
          </Card>
        </>
      )}

      <AccrualDialog
        isOpen={isAccrualDialogOpen}
        onOpenChange={setIsAccrualDialogOpen}
        providers={manualProviders}
        formData={accrualForm}
        onFormDataChange={setAccrualForm}
        onSubmit={handleAccrualSubmit}
        isEditing={Boolean(editingAccrual)}
        isSubmitting={isSubmitting}
        formatMoney={formatMoney}
      />

      <SettlementDialog
        isOpen={isSettlementDialogOpen}
        onOpenChange={setIsSettlementDialogOpen}
        providers={providers}
        formData={formData}
        onFormDataChange={setFormData}
        onSubmit={handleSubmit}
        isEditing={Boolean(editingSettlement)}
        isSubmitting={isSubmitting}
        isBankConfirmed={Boolean(editingSettlement?.bank_fingerprint)}
      />

      <StatementImportDialog
        isOpen={isImportOpen}
        onOpenChange={setIsImportOpen}
        onImported={refresh}
        formatMoney={formatMoney}
      />

      <CommissionsDialog
        isOpen={isCommissionsDialogOpen}
        onOpenChange={setIsCommissionsDialogOpen}
        providers={providers}
        onUpdate={handleUpdateProvider}
      />
    </div>
  );
}
